import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const toCoLocal = (iso) => (iso ? new Date(new Date(iso).getTime() - 5 * 3600e3).toISOString().slice(0, 16) : '');
const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
const EMPTY = {
  name: '', kind: 'free_over', value: '', min_subtotal: '', starts_at: '', ends_at: '', zones: [], seller_ids: '',
  automarket_pct: '100', per_order_cap: '', campaign_budget: '', monthly_budget: '', max_uses_per_buyer: '',
  max_pct_of_commission: '', min_discount: '', notes: '',
};

export const fromPromo = (p) => ({
  ...EMPTY, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v == null ? '' : v])),
  starts_at: toCoLocal(p.starts_at), ends_at: toCoLocal(p.ends_at),
  zones: (p.zones || []).map((z) => ({ department: z.department, city: z.city || '' })),
  seller_ids: (p.seller_ids || []).join(', '), automarket_pct: String(p.automarket_pct ?? 100),
});

const toPayload = (f) => ({
  name: f.name, kind: f.kind, value: f.kind === 'free_over' ? null : numOrNull(f.value),
  min_subtotal: numOrNull(f.min_subtotal), starts_at: f.starts_at || null, ends_at: f.ends_at || null,
  zones: f.zones.filter((z) => z.department).map((z) => ({ department: z.department, city: z.city || null })),
  seller_ids: String(f.seller_ids || '').split(',').map((s) => s.trim()).filter(Boolean),
  automarket_pct: Number(f.automarket_pct || 0), per_order_cap: numOrNull(f.per_order_cap),
  campaign_budget: numOrNull(f.campaign_budget), monthly_budget: numOrNull(f.monthly_budget),
  max_uses_per_buyer: numOrNull(f.max_uses_per_buyer), max_pct_of_commission: numOrNull(f.max_pct_of_commission),
  min_discount: Number(f.min_discount || 0), notes: f.notes || null,
});

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const ZonesEditor = ({ zones, setZones, departments }) => (
  <div data-testid="promo-zones">
    {zones.map((z, i) => (
      <div key={i} className="mb-2 flex gap-2">
        <select value={z.department} onChange={(e) => setZones(zones.map((x, j) => (j === i ? { ...x, department: e.target.value } : x)))}
          className="h-9 flex-1 border border-slate-300 bg-white px-2 text-sm" data-testid={`promo-zone-department-${i}`}>
          <option value="">Departamento…</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <Input value={z.city} placeholder="Ciudad (opcional)" className="h-9 flex-1 rounded-none"
          onChange={(e) => setZones(zones.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)))} data-testid={`promo-zone-city-${i}`} />
        <Button size="sm" variant="ghost" onClick={() => setZones(zones.filter((_, j) => j !== i))} data-testid={`promo-zone-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button size="sm" variant="outline" className="rounded-none" onClick={() => setZones([...zones, { department: '', city: '' }])} data-testid="promo-zone-add">
      <Plus className="mr-1 h-4 w-4" /> Agregar zona
    </Button>
    <p className="mt-1 text-[11px] text-slate-400">Sin zonas = todo el país con cobertura. Ciudad vacía = todo el departamento.</p>
  </div>
);

export const PromoForm = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial ? fromPromo(initial) : EMPTY); }, [open, initial]);
  useEffect(() => { api.get('/geo/departments').then(({ data }) => setDepartments(data.items || [])).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const body = toPayload(f);
      const { data } = initial
        ? await api.put(`/admin/shipping-promotions/${initial.promo_id}`, body)
        : await api.post('/admin/shipping-promotions', body);
      toast.success(initial ? (data.acceptances_reset ? 'Guardada · los vendedores deben aceptar de nuevo' : 'Promoción actualizada') : 'Promoción creada en borrador');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (Array.isArray(d) ? String(d[0]?.msg || '').replace(/^Value error,\s*/, '') : d) || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const sellerPct = 100 - Number(f.automarket_pct || 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none" data-testid="promo-form-dialog">
        <DialogHeader><DialogTitle>{initial ? 'Editar promoción' : 'Nueva promoción de envío'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Nombre"><Input className="rounded-none" value={f.name} onChange={set('name')} data-testid="promo-name-input" /></Field></div>
          <Field label="Tipo">
            <select className="h-9 w-full border border-slate-300 bg-white px-2 text-sm" value={f.kind} onChange={set('kind')} data-testid="promo-kind-select">
              <option value="free_over">Envío gratis desde un monto</option>
              <option value="percent">Descuento en porcentaje</option>
              <option value="fixed">Descuento en valor fijo (COP)</option>
            </select>
          </Field>
          {f.kind !== 'free_over'
            ? <Field label={f.kind === 'percent' ? 'Porcentaje (%)' : 'Valor fijo (COP)'}><Input type="number" min="0" className="rounded-none" value={f.value} onChange={set('value')} data-testid="promo-value-input" /></Field>
            : <div />}
          <Field label="Monto mínimo de compra (COP)" hint={f.kind === 'free_over' ? 'Obligatorio. Subtotal de productos del vendedor, sin envío.' : 'Opcional.'}>
            <Input type="number" min="0" className="rounded-none" value={f.min_subtotal} onChange={set('min_subtotal')} data-testid="promo-min-subtotal-input" />
          </Field>
          <Field label="Tope de descuento por pedido (COP)" hint="Opcional."><Input type="number" min="0" className="rounded-none" value={f.per_order_cap} onChange={set('per_order_cap')} data-testid="promo-per-order-cap-input" /></Field>
          <Field label="Inicio (hora Colombia)"><Input type="datetime-local" className="rounded-none" value={f.starts_at} onChange={set('starts_at')} data-testid="promo-starts-input" /></Field>
          <Field label="Fin (hora Colombia)" hint="Opcional."><Input type="datetime-local" className="rounded-none" value={f.ends_at} onChange={set('ends_at')} data-testid="promo-ends-input" /></Field>
          <Field label="% que asume AutoMarket" hint={`Vendedor asume ${sellerPct}%${sellerPct > 0 ? ' · requiere su aceptación' : ''}`}>
            <Input type="number" min="0" max="100" className="rounded-none" value={f.automarket_pct} onChange={set('automarket_pct')} data-testid="promo-automarket-pct-input" />
          </Field>
          <Field label="Presupuesto total de campaña (COP)" hint="Obligatorio para activar si AutoMarket asume parte."><Input type="number" min="0" className="rounded-none" value={f.campaign_budget} onChange={set('campaign_budget')} data-testid="promo-campaign-budget-input" /></Field>
          <Field label="Presupuesto mensual de campaña (COP)" hint="Opcional."><Input type="number" min="0" className="rounded-none" value={f.monthly_budget} onChange={set('monthly_budget')} data-testid="promo-monthly-budget-input" /></Field>
          <Field label="Usos máximos por comprador" hint="Opcional."><Input type="number" min="1" className="rounded-none" value={f.max_uses_per_buyer} onChange={set('max_uses_per_buyer')} data-testid="promo-max-uses-input" /></Field>
          <Field label="Máx. subsidio AutoMarket (% de su comisión)" hint="Opcional."><Input type="number" min="0" max="100" className="rounded-none" value={f.max_pct_of_commission} onChange={set('max_pct_of_commission')} data-testid="promo-max-commission-pct-input" /></Field>
          <Field label="Descuento mínimo para aplicar (COP)" hint="Si un límite lo recorta por debajo, no se aplica."><Input type="number" min="0" className="rounded-none" value={f.min_discount} onChange={set('min_discount')} data-testid="promo-min-discount-input" /></Field>
          <div className="sm:col-span-2"><Field label="Zonas"><ZonesEditor zones={f.zones} setZones={(zones) => setF({ ...f, zones })} departments={departments} /></Field></div>
          <div className="sm:col-span-2"><Field label="Vendedores participantes (IDs separados por coma)" hint="Vacío = todos los vendedores."><Input className="rounded-none" value={f.seller_ids} onChange={set('seller_ids')} data-testid="promo-seller-ids-input" /></Field></div>
          <div className="sm:col-span-2"><Field label="Notas internas"><Input className="rounded-none" value={f.notes} onChange={set('notes')} data-testid="promo-notes-input" /></Field></div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="promo-cancel-btn">Cancelar</Button>
          <Button className="rounded-none bg-slate-900" disabled={saving} onClick={save} data-testid="promo-save-btn">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
